/* Rinashi fitment system.
 *
 * Data source: an inline JSON blob (<script data-fitment-data>) rendered by
 * snippets/fitment-module.liquid from the `vehicle` metaobjects. The chosen
 * vehicle is persisted to localStorage so the verdict follows the shopper
 * across PDPs, collection cards, and cart line items — those surfaces read
 * the persisted record and need no blob of their own.
 *
 * Deferred module; no dependencies.
 */
(() => {
  'use strict';

  // fitment.js is intentionally available globally and is also referenced by
  // some legacy sections. Guard the IIFE so duplicate script tags cannot bind
  // duplicate selectors, observers, or cart-attribute requests.
  if (window.__rinashiFitmentInitialized) return;
  window.__rinashiFitmentInitialized = true;

  const STORE_KEY = 'rinashi:vehicle';
  const VERSION = 2;
  const KLAVIYO_COMPANY_ID = 'Vhg7NB';
  const FITMENT_REQUEST_METRIC = 'Fitment Request Submitted';
  const REVIEW_SETUP_PATTERN = /\b(email|e-mail|contact|ask|confirm|review|photo|verify|unknown|tbd)\b/i;
  const REVIEW_NOTE_PATTERN =
    /\b(email|e-mail|contact|ask|confirm|review|photo|verify|unknown|tbd|bench(?:[- ]seats?)?|power(?:[- ]seats?)?|if unsure|var(?:y|ies)|differ(?:ent|ence|ences|s)?|modified|custom)\b/i;
  const OTHER_PRODUCT_PATTERN = /\b(phone (?:mount|holder)|gauge|clock|fuel gauge|windscreen|suction mount|console cup holder)\b/i;

  /* ---------- persistence ---------- */

  const migrateSelection = (vehicle) => {
    if (!vehicle || vehicle.handle !== 'porsche-914-944-928') return vehicle;
    return {
      ...vehicle,
      name: 'Porsche 944 / 968 / 928 (1978–1995)',
      model: '944 / 968 / 928',
      yearStart: 1978,
    };
  };

  const readSelection = () => {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed.v === VERSION ? migrateSelection(parsed.vehicle) : null;
    } catch {
      return null;
    }
  };

  const writeSelection = (vehicle) => {
    try {
      if (vehicle) localStorage.setItem(STORE_KEY, JSON.stringify({ v: VERSION, vehicle }));
      else localStorage.removeItem(STORE_KEY);
    } catch {
      /* private mode — selection just won't persist */
    }
    captureToCart(vehicle);
    document.dispatchEvent(new CustomEvent('rinashi:vehicle-changed', { detail: { vehicle } }));
  };

  /* ---------- order capture ----------
   * Write the shopper's car onto the cart as an order attribute, so every
   * order arrives with "Vehicle: <car>" — fulfillment can double-check fit
   * before shipping, and the fitment dataset builds itself. Fire-and-forget;
   * never blocks the UI. The session guard stops a re-POST on every page. */
  const CAPTURE_GUARD = 'rinashi:vehicle-on-cart';
  let captureQueue = Promise.resolve();

  const captureToCart = (vehicle, force = true) => {
    try {
      const label = vehicle ? vehicle.name : '';
      if (!force && sessionStorage.getItem(CAPTURE_GUARD) === label) return;
      // Make changes and model selections can happen faster than the network.
      // Serialize writes so an earlier "clear" response cannot overwrite the
      // vehicle selected immediately afterward.
      captureQueue = captureQueue
        .catch(() => {})
        .then(() =>
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { Vehicle: label } }),
          })
        )
        .then(() => {
          try {
            sessionStorage.setItem(CAPTURE_GUARD, label);
          } catch {
            /* non-critical */
          }
        })
        .catch(() => {});
    } catch {
      /* non-critical */
    }
  };

  /* ---------- data ---------- */

  let vehiclesCache = null;
  const getVehicles = () => {
    if (vehiclesCache) return vehiclesCache;
    const blob = document.querySelector('[data-fitment-data]');
    if (!blob) return [];
    try {
      vehiclesCache = JSON.parse(blob.textContent).vehicles || [];
    } catch {
      vehiclesCache = [];
    }
    return vehiclesCache;
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const getFitmentProductIds = (root) =>
    [root.dataset.productId, root.dataset.fitmentInheritsProductId]
      .map((id) => Number(id || 0))
      .filter(Boolean);

  const isRecordedForProduct = (root, vehicle) => {
    const productIds = getFitmentProductIds(root);
    if (!productIds.length) return true;
    return (
      Array.isArray(vehicle.products) &&
      productIds.some((productId) => vehicle.products.includes(productId))
    );
  };

  const removeOtherProductSentences = (text) => {
    const sentences = String(text || '')
      .replace(/([.!?])\s+(?=[A-Z])/g, '$1\n')
      .split(/\n+/);
    return sentences
      .map((sentence) => sentence.trim())
      .filter((sentence) => sentence && !OTHER_PRODUCT_PATTERN.test(sentence))
      .join(' ');
  };

  const setPlaceholder = (root, show) => {
    const p = root.querySelector('[data-fitment-placeholder]');
    if (p) p.hidden = !show;
  };

  const setInquiry = (root, show) => {
    const inquiry = root.querySelector('[data-fitment-inquiry]');
    if (inquiry) inquiry.hidden = !show;
  };

  const initInquiry = (root) => {
    const vehicleInput = root.querySelector('[data-fitment-inquiry-vehicle]');
    const bodyInput = root.querySelector('[data-fitment-inquiry-body]');
    if (!vehicleInput || !bodyInput) return;

    const form = vehicleInput.closest('form');

    const updateBody = () => {
      const product = root.dataset.productTitle ? ` for ${root.dataset.productTitle}` : '';
      const vehicle = vehicleInput.value.trim() || '[vehicle not entered]';
      bodyInput.value = `Fitment request${product}. Vehicle: ${vehicle}.`;
    };

    vehicleInput.addEventListener('input', updateBody);
    if (form && !form.dataset.klaviyoFitmentBound) {
      form.dataset.klaviyoFitmentBound = 'true';
      form.addEventListener('submit', () => {
        const email = form.querySelector('[name="contact[email]"]')?.value.trim();
        if (!email) return;

        const firstName = form.querySelector('[name="contact[name]"]')?.value.trim() || '';
        const vehicle = vehicleInput.value.trim() || 'Vehicle not provided';
        const product = form.querySelector('[name="contact[Product]"]')?.value || 'General fitment inquiry';
        const sourcePage = form.querySelector('[name="contact[Source page]"]')?.value || window.location.href;
        const properties = {
          Vehicle: vehicle,
          Product: product,
          'Source Page': sourcePage,
        };

        fetch(`https://a.klaviyo.com/client/events/?company_id=${KLAVIYO_COMPANY_ID}`, {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision: '2026-04-15',
          },
          keepalive: true,
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                properties,
                metric: {
                  data: {
                    type: 'metric',
                    attributes: { name: FITMENT_REQUEST_METRIC },
                  },
                },
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      first_name: firstName,
                      properties: {
                        'Fitment Vehicle': vehicle,
                        'Fitment Product': product,
                        'Fitment Source Page': sourcePage,
                      },
                    },
                  },
                },
              },
            },
          }),
        }).catch(() => {});
      });
    }
    updateBody();
  };

  /* ---------- selector module ---------- */

  const initSelector = (root) => {
    let vehicles = getVehicles();
    if (!vehicles.length) return;

    const makeSelect = root.querySelector('[data-fitment-make]');
    const modelSelect = root.querySelector('[data-fitment-model]');
    const verdict = root.querySelector('[data-fitment-verdict]');
    if (!makeSelect || !modelSelect || !verdict) return;

    // Display only: the model dropdown is emptied + disabled once the shopper
    // picks "My car isn't listed" — hide it entirely instead of leaving a dead,
    // unclickable control on screen. (No effect on fitment matching.)
    const modelField = modelSelect.closest('div');
    const setModelFieldVisible = (show) => {
      if (modelField) modelField.hidden = !show;
    };

    initInquiry(root);

    // Per-product scoping: when data-fitment-scope="product", show only the
    // vehicles this product is confirmed to fit — so a Porsche-911-only part
    // never offers Alfa/BMW/Datsun, and only the models that actually fit.
    const scopeIds = getFitmentProductIds(root);
    if (root.dataset.fitmentScope === 'product' && scopeIds.length) {
      vehicles = vehicles.filter((vehicle) => isRecordedForProduct(root, vehicle));
      if (!vehicles.length) return;
    }

    const makes = [...new Set(vehicles.map((v) => v.make))].sort();
    makeSelect.innerHTML =
      `<option value="">${escapeHtml(makeSelect.dataset.placeholder || 'Make')}</option>` +
      makes.map((m) => `<option value="${escapeHtml(m)}">${escapeHtml(m)}</option>`).join('') +
      `<option value="__other__">${escapeHtml(root.dataset.unknownLabel || "My car isn't listed")}</option>`;

    const fillModels = (make) => {
      const models = vehicles.filter((v) => v.make === make);
      modelSelect.innerHTML =
        `<option value="">${escapeHtml(modelSelect.dataset.placeholder || 'Model / era')}</option>` +
        models.map((v) => `<option value="${escapeHtml(v.handle)}">${escapeHtml(v.name)}</option>`).join('');
      modelSelect.disabled = false;
    };

    makeSelect.addEventListener('change', () => {
      // A new make is not the old car. Clear both persistence and the cart
      // attribute before showing another set of models or the inquiry form.
      writeSelection(null);

      if (makeSelect.value === '__other__') {
        modelSelect.innerHTML = '';
        modelSelect.disabled = true;
        setModelFieldVisible(false);
        const vehicleInput = root.querySelector('[data-fitment-inquiry-vehicle]');
        if (vehicleInput) {
          vehicleInput.value = '';
          vehicleInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
        renderUnknown(root, verdict);
        return;
      }
      setModelFieldVisible(true);
      setInquiry(root, false);
      verdict.hidden = true;
      setPlaceholder(root, true);
      if (!makeSelect.value) {
        modelSelect.innerHTML = '';
        modelSelect.disabled = true;
        return;
      }
      fillModels(makeSelect.value);
    });

    modelSelect.addEventListener('change', () => {
      const vehicle = vehicles.find((v) => v.handle === modelSelect.value);
      if (vehicle) {
        writeSelection(vehicle);
        return;
      }

      writeSelection(null);
      verdict.hidden = true;
      verdict.replaceChildren();
      setInquiry(root, false);
      setPlaceholder(root, true);
    });

    const inquiry = root.querySelector('[data-fitment-inquiry]');
    if (inquiry && inquiry.hasAttribute('data-fitment-force-open')) {
      writeSelection(null);
      makeSelect.value = '__other__';
      modelSelect.innerHTML = '';
      modelSelect.disabled = true;
      setModelFieldVisible(false);
      renderUnknown(root, verdict);
      return;
    }

    // Restore a persisted selection into the controls.
    const saved = readSelection();
    const currentSavedVehicle = saved && vehicles.find((v) => v.handle === saved.handle);
    if (currentSavedVehicle) {
      makeSelect.value = currentSavedVehicle.make;
      fillModels(currentSavedVehicle.make);
      modelSelect.value = currentSavedVehicle.handle;
      renderVerdict(root, verdict, currentSavedVehicle);
    } else if (makes.length === 1) {
      // Scoped to a single make (e.g. Porsche-only): pre-select it so the
      // shopper only has to pick the model.
      makeSelect.value = makes[0];
      fillModels(makes[0]);
    }
  };

  const renderUnknown = (root, verdict) => {
    root.dataset.fitmentState = 'unlisted';
    verdict.hidden = true;
    verdict.replaceChildren();
    setPlaceholder(root, false);
    setInquiry(root, true);
  };

  const renderVerdict = (root, verdict, vehicle) => {
    setInquiry(root, false);
    const productId = Number(root.dataset.productId || 0);
    const email = root.dataset.contactEmail || 'info@rinashi.com';
    const confirmedHere = !productId || isRecordedForProduct(root, vehicle);
    const needsConfirmation = confirmedHere && vehicle.verified === false;
    const compact = root.dataset.fitmentResultStyle === 'compact';
    const cupHolderContext = root.dataset.fitmentFamily === 'cup-holder';
    const bracket = String(vehicle.bracket || '').trim();
    // Current records append an unverified packing-list assertion to the setup.
    // Keep the useful configuration text in compact results without promising
    // that a specific component is present in the current sold package.
    const displayBracket = bracket
      .replace(/\s*(?:\(\s*)?included in (?:the )?kit(?:\s*\))?/gi, '')
      .trim();
    const notes = String(vehicle.notes || '').trim();
    const proof = String(vehicle.quote || '').trim();
    const relevantNotes = cupHolderContext ? removeOtherProductSentences(notes) : notes;
    const relevantProof =
      cupHolderContext && OTHER_PRODUCT_PATTERN.test(proof) ? '' : proof;
    const hasDisplayableSetup = Boolean(
      displayBracket && !REVIEW_SETUP_PATTERN.test(displayBracket)
    );
    const hasReviewCondition = REVIEW_NOTE_PATTERN.test(relevantNotes);
    const hasMixedProductData =
      !cupHolderContext &&
      (Boolean(vehicle.mountVariant) ||
        Number(vehicle.gauge || 0) > 0 ||
        OTHER_PRODUCT_PATTERN.test(`${notes} ${proof}`));
    const needsCompactReview =
      compact &&
      (!confirmedHere ||
        vehicle.verified !== true ||
        !hasDisplayableSetup ||
        hasReviewCondition ||
        hasMixedProductData);
    const needsReview = needsConfirmation || needsCompactReview;

    let html = '';
    if (confirmedHere && !needsReview) {
      if (compact) {
        // The current metaobject flag only distinguishes seed-tagged rows. It
        // is not a physical-fit certification, so compact results stay neutral
        // and describe the record rather than presenting a green-check verdict.
        html += `<p class="fitment-verdict__unconfirmed"><strong>Current cup-holder record: ${escapeHtml(vehicle.name)}</strong></p>`;
        html += `<p class="fitment-verdict__line"><strong>Bracket listed in the record:</strong> ${escapeHtml(displayBracket)}</p>`;
        html += '<p class="fitment-verdict__line">This storefront record is not a fit guarantee. Check the actual rail, fastener, full seat travel, controls, passenger space, floor support and intended container.</p>';
      } else {
        html += `<div class="fitment-verdict__confirmed"><span class="fitment-verdict__ring" aria-hidden="true">✓</span><span class="fitment-verdict__name">Fits your ${escapeHtml(vehicle.name)}</span></div>`;
      }
      if (!compact && root.dataset.fitmentResultStyle !== 'minimal') {
        if (vehicle.bracket) html += `<p class="fitment-verdict__line">${escapeHtml(vehicle.bracket)}</p>`;
        if (relevantNotes) html += `<p class="fitment-verdict__line fitment-verdict__notes">${escapeHtml(relevantNotes)}</p>`;
        if (relevantProof) html += `<p class="fitment-verdict__quote">“${escapeHtml(relevantProof)}”</p>`;
        if (vehicle.url) html += `<p class="fitment-verdict__line"><a href="${escapeHtml(vehicle.url)}">Everything we fit on the ${escapeHtml(vehicle.name)} →</a></p>`;
      }
      root.dataset.fitmentState = 'recorded';
    } else if (needsReview) {
      if (compact) {
        html += `<p class="fitment-verdict__unconfirmed"><strong>Needs fit review: ${escapeHtml(vehicle.name)}</strong></p>`;
        html += '<p class="fitment-verdict__line">The current record needs a product-specific review. Use the free fit-help form before ordering.</p>';
      } else {
        // Confidence-led, not a stop sign: the car IS on this product's fit
        // list; the flag only means the setup details need double-checking.
        html += `<p class="fitment-verdict__unconfirmed"><strong>Built to fit your ${escapeHtml(vehicle.name)}.</strong></p>`;
        if (relevantNotes) html += `<p class="fitment-verdict__line fitment-verdict__notes">${escapeHtml(relevantNotes)}</p>`;
        html += '<p class="fitment-verdict__line">Order now — your car goes on the order and we double-check the fit before it ships. Prefer to ask first? Use the short form below; real answer within a day.</p>';
        if (vehicle.url) html += `<p class="fitment-verdict__line"><a href="${escapeHtml(vehicle.url)}">Everything we fit on the ${escapeHtml(vehicle.name)} →</a></p>`;
      }
      root.dataset.fitmentState = 'needs-review';
    } else {
      const subject = encodeURIComponent(`Does this fit my ${vehicle.name}?`);
      html += `<p class="fitment-verdict__unconfirmed">We haven't confirmed this product on the ${escapeHtml(vehicle.name)} yet.</p>`;
      html += `<p class="fitment-verdict__line"><a class="link" href="mailto:${escapeHtml(email)}?subject=${subject}">Ask us — real answer within a day</a>${vehicle.url ? ` · <a href="${escapeHtml(vehicle.url)}">See what fits your car →</a>` : ''}</p>`;
      root.dataset.fitmentState = 'not-recorded';
    }
    verdict.setAttribute('role', 'status');
    verdict.innerHTML = html;
    verdict.hidden = false;
    setPlaceholder(root, false);
    setInquiry(root, needsReview);

    if (needsReview) {
      const vehicleInput = root.querySelector('[data-fitment-inquiry-vehicle]');
      if (vehicleInput) {
        vehicleInput.value = vehicle.name;
        vehicleInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  };

  /* ---------- badges (collection cards, cart lines) ---------- */

  const applyBadges = () => {
    const vehicle = readSelection();
    document.querySelectorAll('[data-fitment-product-id]').forEach((el) => {
      const fits = Boolean(
        vehicle &&
          vehicle.verified !== false &&
          Array.isArray(vehicle.products) &&
          vehicle.products.includes(Number(el.dataset.fitmentProductId))
      );
      // Only write on change — the MutationObserver below watches the DOM,
      // and unconditional writes would loop it.
      if (el.hidden !== !fits) el.hidden = !fits;
      if (fits) {
        const text = `✓ Fits your ${vehicle.name}`;
        if (el.textContent !== text) el.textContent = text;
      }
    });
  };

  /* ---------- variant auto-selection (911 mount + bundles) ---------- */

  const autoSelectVariant = () => {
    const vehicle = readSelection();
    document.querySelectorAll('[data-fitment-variant-scope]').forEach((scope) => {
      const note = scope.querySelector('[data-fitment-variant-note]');
      if (!vehicle || vehicle.verified === false || !vehicle.mountVariant) {
        if (note) note.hidden = true;
        return;
      }
      let target = null;
      scope.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (input.value === vehicle.mountVariant) target = input;
      });
      if (target && !target.checked) {
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (!target) {
        // Dropdown-style picker: select the option instead.
        scope.querySelectorAll('select').forEach((select) => {
          const option = [...select.options].find((o) => o.value === vehicle.mountVariant);
          if (option && select.value !== option.value) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (option) target = select;
        });
      }
      if (note && target) {
        const gauge = vehicle.gauge ? `, ${vehicle.gauge} mm` : '';
        const text = `${vehicle.name} → ${vehicle.mountVariant}${gauge} — selected for you. Override below if needed.`;
        if (note.textContent !== text) note.textContent = text;
        if (note.hidden) note.hidden = false;
      }
    });
  };

  /* ---------- lite video (click-to-load YouTube embed) ---------- */

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-lite-video]');
    if (!button) return;
    const id = button.dataset.liteVideo;
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?rel=0&modestbranding=1&playsinline=1&autoplay=1`;
    iframe.title = button.getAttribute('aria-label') || 'Install video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    button.replaceChildren(iframe);
    button.disabled = true;
    button.style.cursor = 'default';
  });

  /* ---------- plate-size selector (informational; no variant change) ---------- */

  const initPlateSelect = (root) => {
    const guide = root.querySelector('[data-plate-guide]');
    const opts = [...root.querySelectorAll('.plate-opt')];
    opts.forEach((opt) => {
      opt.addEventListener('click', () => {
        opts.forEach((o) => {
          o.classList.remove('is-active');
          o.setAttribute('aria-pressed', 'false');
        });
        opt.classList.add('is-active');
        opt.setAttribute('aria-pressed', 'true');
        if (guide && opt.dataset.guide) guide.textContent = opt.dataset.guide;
      });
    });
  };

  /* ---------- car-specific proof (float matching reviews to front) ---------- */

  const applyCarProof = () => {
    const root = document.querySelector('[data-car-proof]');
    if (!root) return;
    const vehicle = readSelection();
    const vehicleMake = vehicle && vehicle.make ? vehicle.make.trim().toLowerCase() : '';
    const heading = root.querySelector('[data-car-proof-heading]');
    let matches = 0;
    root.querySelectorAll('[data-car-proof-card]').forEach((card) => {
      const make = (card.dataset.make || '').trim().toLowerCase();
      const isMatch = Boolean(vehicleMake && make && make === vehicleMake);
      card.classList.toggle('is-match', isMatch);
      const badge = card.querySelector('[data-car-proof-match]');
      if (badge) badge.hidden = !isMatch;
      if (isMatch) matches += 1;
    });
    if (heading) {
      const next = vehicle && matches > 0 ? `Owners with your ${vehicle.name}` : heading.dataset.default || '';
      if (next && heading.textContent !== next) heading.textContent = next;
    }
  };

  /* ---------- boot ---------- */

  const boot = () => {
    // Vehicle landing pages pin the selection to their vehicle.
    const setter = document.querySelector('[data-fitment-set-vehicle]');
    if (setter) {
      const vehicle = getVehicles().find((v) => v.handle === setter.dataset.fitmentSetVehicle);
      const current = readSelection();
      if (vehicle && (!current || current.handle !== vehicle.handle)) writeSelection(vehicle);
    }

    document.querySelectorAll('[data-fitment-selector]').forEach(initSelector);
    document.querySelectorAll('[data-plate-select]').forEach(initPlateSelect);
    applyBadges();
    autoSelectVariant();
    applyCarProof();

    // Returning shopper with a saved car: make sure it's on the current cart
    // (once per session — a fresh cart wouldn't have the attribute yet).
    const savedVehicle = readSelection();
    if (savedVehicle) captureToCart(savedVehicle, false);

    // Cart drawer and quick-add re-render DOM after fetches; re-apply on
    // childList changes. applyBadges/autoSelectVariant only write on diff,
    // so this cannot loop.
    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        applyBadges();
        autoSelectVariant();
      });
    }).observe(document.body, { subtree: true, childList: true });
  };

  document.addEventListener('rinashi:vehicle-changed', (event) => {
    applyBadges();
    autoSelectVariant();
    applyCarProof();
    // Re-render every verdict on the page (a PDP may host two modules).
    document.querySelectorAll('[data-fitment-selector]').forEach((root) => {
      const verdict = root.querySelector('[data-fitment-verdict]');
      if (!verdict) return;
      if (event.detail.vehicle) {
        renderVerdict(root, verdict, event.detail.vehicle);
        return;
      }
      root.dataset.fitmentState = 'unselected';
      verdict.hidden = true;
      verdict.replaceChildren();
      setInquiry(root, false);
      setPlaceholder(root, true);
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
